import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import Modal, { ModalTransition } from "@atlaskit/modal-dialog";
import AvatarGroup from "@atlaskit/avatar-group";

import AvatarListDialog from "./AvatarListDialog";
import fakePeopleData from "../../../fakePeopleData";
import EditableText from "../../inline-edit/EditableText";
import EditableLine from "../../inline-edit/EditableLine";
import {
  AnnoucementDateContainer,
  AnnouncementAttendButton,
  AnnouncementContainer,
  AnnouncementContentContainer,
  AnnouncementCover,
  AnnouncementDate,
  AnnouncementHeader,
  AnnouncementHeading,
  AnnouncementInformationContainer,
  AnnouncementInformationsContainer,
  AnnouncementInformationSpan,
  AnnouncementParticipantsContainer,
} from "./styles";
import { useParticipants } from "../../hooks/use-participants";
import {
  getContentProperty,
  updateContentProperty,
} from "../../../announcements-api";
import { buildDate, buildDateAndSplit } from "../../../dateFormatting";
import { useAtlassianLocale } from "@Shared/Hooks/useAtlassianLocale";
import { LAYOUT_STRUCTURE_OPTIONS } from "../../../../../optics/components/drawers/settings/features/announcements/defaults";
import { Arrow, Date, Location } from "./Icons";
import { handleLinkClick } from "../../../../../helpers/handle-link-click";

const Image = ({ data, config, viewMode = "view", isEditable }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [hasCurrentUserAccepted, setHasCurrentUserAccepted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useParticipants(
    { viewMode, data, config },
    { setParticipants, setCurrentUser, setHasCurrentUserAccepted }
  );

  const { t } = useTranslation();
  const theme = useTheme();

  const [locale] = useAtlassianLocale();
  const { title, linkText, date, location, color, textColor } = data;
  const { borderRadius, layoutStructure } = config;

  const isList = config.view === "list";

  const hasLink = viewMode === "view" && !!data?.url?.link;

  const builtDate = buildDate(date, false, locale, config.designStyle);
  const builtDateSplit = buildDateAndSplit(
    date,
    false,
    locale,
    config.designStyle
  );

  const handleAcceptInvite = async () => {
    try {
      setParticipants([{ ...currentUser }, ...participants]);
      setHasCurrentUserAccepted(true);
      const contentProperty = await getContentProperty(data.id);
      if (
        contentProperty.data.propertyExists &&
        !!contentProperty.data.body.results.length
      ) {
        const {
          value: { participants },
          version: { number },
        } = contentProperty.data.body.results[0];
        await updateContentProperty(
          `caelor-${data.id}`,
          {
            participants: [currentUser.key, ...participants],
          },
          number + 1
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRevokeInvite = async () => {
    try {
      setParticipants([
        ...participants.filter(
          (participantId) => participantId.key !== currentUser.key
        ),
      ]);
      setHasCurrentUserAccepted(false);
      const contentProperty = await getContentProperty(data.id);
      if (
        contentProperty.data.propertyExists &&
        !!contentProperty.data.body.results.length
      ) {
        const {
          value: { participants },
          version: { number },
        } = contentProperty.data.body.results[0];
        await updateContentProperty(
          `caelor-${data.id}`,
          {
            participants: [
              ...participants.filter(
                (participantId) => participantId !== currentUser.key
              ),
            ],
          },
          number + 1
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <AnnouncementContainer
        borderRadius={borderRadius}
        hasLink={hasLink}
        onClick={() => {
          if (hasLink) {
            handleLinkClick({
              url: data?.url || {},
              newTab: data?.newTab === "true",
            });
          }
        }}
      >
        <AnnouncementContentContainer isList={isList}>
          <div style={{ position: "relative" }}>
            <AnnouncementCover
              src={data.image.link}
              position={data.image.focalPoint}
              borderRadius={borderRadius}
              alt={data.title}
              isList={isList}
            />

            {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.DATE_IN_IMAGE) &&
              !!Object.keys(builtDate).length && (
                <AnnoucementDateContainer>
                  <AnnouncementDate
                    backgroundColor={color[theme.name]}
                    isList={isList}
                  >
                    <span
                      style={{
                        textTransform: "uppercase",
                        color: textColor[theme.name],
                      }}
                    >
                      {builtDateSplit?.month}
                    </span>
                    <span
                      style={{ fontSize: "25px", color: textColor[theme.name] }}
                    >
                      {builtDateSplit?.day}
                    </span>
                  </AnnouncementDate>
                </AnnoucementDateContainer>
              )}
          </div>
          <AnnouncementHeader>
            <AnnouncementHeading isList={isList}>
              <EditableText
                announcementId={data.id}
                isEditable={isEditable}
                value={title}
                propKey="title"
                minHeight={isList ? "1em" : "2.2em"}
              />
            </AnnouncementHeading>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <AnnouncementParticipantsContainer>
                {layoutStructure.includes(
                  LAYOUT_STRUCTURE_OPTIONS.PARTICIPANTS
                ) && (
                  <AvatarGroup
                    data={viewMode === "edit" ? fakePeopleData : participants}
                    appearance="stack"
                    maxCount={isList ? 6 : 4}
                    size="medium"
                    onAvatarClick={(e) => {
                      e.preventDefault();
                    }}
                    onMoreClick={(e) => {
                      e.preventDefault();
                      if (viewMode === "view") {
                        setIsModalOpen(true);
                      }
                    }}
                  />
                )}

                {layoutStructure.includes(
                  LAYOUT_STRUCTURE_OPTIONS.PARTICIPANTS
                ) && (
                  <AnnouncementAttendButton
                    bgClr={color[theme.name]}
                    textClr={textColor[theme.name]}
                    inverse={hasCurrentUserAccepted}
                    onClick={(e) => {
                      if (viewMode === "view") {
                        e.preventDefault();
                        e.stopPropagation();
                        hasCurrentUserAccepted
                          ? handleRevokeInvite()
                          : handleAcceptInvite();
                      }
                    }}
                  >
                    {t(hasCurrentUserAccepted ? t("unattend") : t("attend"))}
                  </AnnouncementAttendButton>
                )}
              </AnnouncementParticipantsContainer>

              {isList && (
                <AnnouncementInformationsContainer>
                  {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.DATE) && (
                    <AnnouncementInformationContainer>
                      <Date color={theme.border.black50} />
                      <AnnouncementInformationSpan>
                        {builtDate}
                      </AnnouncementInformationSpan>
                    </AnnouncementInformationContainer>
                  )}

                  {layoutStructure.includes(
                    LAYOUT_STRUCTURE_OPTIONS.LOCATION
                  ) && (
                    <AnnouncementInformationContainer>
                      <Location color={theme.border.black50} />
                      <AnnouncementInformationSpan>
                        {location}
                      </AnnouncementInformationSpan>
                    </AnnouncementInformationContainer>
                  )}
                </AnnouncementInformationsContainer>
              )}
            </div>
          </AnnouncementHeader>
        </AnnouncementContentContainer>

        {!isList && (
          <AnnouncementInformationsContainer>
            {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.DATE) && (
              <AnnouncementInformationContainer>
                <Date color={theme.border.black50} />
                <AnnouncementInformationSpan>
                  {builtDate}
                </AnnouncementInformationSpan>
              </AnnouncementInformationContainer>
            )}

            {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.LOCATION) && (
              <AnnouncementInformationContainer>
                <Location color={theme.border.black50} />
                <AnnouncementInformationSpan>
                  {location}
                </AnnouncementInformationSpan>
              </AnnouncementInformationContainer>
            )}

            {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.LINK) && (
              <AnnouncementInformationContainer>
                <AnnouncementInformationSpan>
                  {data.linkText}
                </AnnouncementInformationSpan>
                {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.ARROW) && (
                  <Arrow fillColor={theme.border.black50} />
                )}
              </AnnouncementInformationContainer>
            )}
          </AnnouncementInformationsContainer>
        )}
      </AnnouncementContainer>

      <ModalTransition>
        {isModalOpen && (
          <Modal
            width="200px"
            isBlanketHidden
            onClose={() => setIsModalOpen(false)}
          >
            <AvatarListDialog avatars={participants} />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export default Image;
